<template>
  <slot v-bind="readonly(field)" />
</template>

<script lang="ts" setup>
import type { Field, Rule } from '#types/components/base/form'
import { FormContextKey } from './context'

defineOptions({
  inheritAttrs: false
})

const props = defineProps<{
  name: string
  rule: Rule
  selector?: string | HTMLElement
}>()

defineSlots<{
  default: (props: Readonly<Field>) => void
}>()

const context = inject(FormContextKey)!
const field = context.getField(props.name)
const instance = getCurrentInstance()
const el = computed<HTMLElement>(() => ['#text', '#comment'].includes(instance?.vnode.el?.nodeName) ? instance?.vnode.el?.nextElementSibling : instance?.vnode.el)

// rules may change regarding user interaction: ie: changing the country may change the validation rules for the zip code
watch(
  () => props.rule,
  (rule) => context?.addRule({ name: props.name, rule }),
  { immediate: true }
)

whenever(() => field.value.firstInvalid && el.value, () => {
  el.value.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true })
  setTimeout(() => {
    /**
     * On iOS, VoiceOver treats an input and its associated label as a group of elements.
     * When the input receives focus, VoiceOver interprets it as focusing the entire group,
     * and highlights the first element (often the label).
     * As a result, the input appears to lose focus.
     * However, when the input is focused again, VoiceOver treats it as moving focus within the group,
     * and focus remains on the input as expected.
     */
    el.value.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true })
  }, 500)
  context.scrollToError && scrollToElement(props.selector || el.value, { preventFocus: true })
  context.resetFirstInvalid(props.name)
})

onUnmounted(() => context?.removeRule(props.name))
</script>
